/**
 * Golem fork (FT-1385): THE TOLD-INFORMATION ROLES — the first-night five
 * (and the Empath's every night) whose action is not a choice but a
 * DELIVERY: the storyteller composes the information and sends it, and the
 * player only receives. FT-1384 dressed the CHOOSING roles (invite → staged
 * → sealed); these roles choose nothing, so their grammar has exactly two
 * beats:
 *
 *   TELLING  the storyteller's Send lands. The information arrives in the
 *            role's own visual language — the candidate coins the sentence
 *            points at light up (the audit's proven gap: targets were
 *            delivered but no coin ever acknowledged them), the number or
 *            character arrives dressed, and the sentence says what it means.
 *            One arrival animation (≤1s), then a held bright pose.
 *
 *   RESIDUE  effects expire, knowledge does not. After a short read window
 *            the telling settles into a quiet persistent trace — dotted
 *            rings, a slack line, dried ink — that SURVIVES THE NIGHT'S END
 *            and stays readable all game (the Empath's threads re-anchor and
 *            re-run each night; everyone else's marks simply hold).
 *
 * No Confirm and no staging for these five: there is nothing to confirm.
 * The FT-1384 receipt-sentence stand-down carries over — the dress IS the
 * receipt.
 *
 * WHO SEES ANY OF THIS: the told player alone. Everything below reads that
 * client's OWN delivered rows (night/playerNight) — a bystander's client
 * never holds them, so there is nothing here to hide from anyone.
 */

/**
 * The five, and the shape of what each is told:
 *   pair        two candidate seats + a character — "one of these two is it"
 *               (the Librarian adds the zero-Outsiders night: no seats, no
 *               character, an empty book).
 *   count       a bare number about the whole table (the Chef's pairs).
 *   neighbours  a number about the seat's own two living neighbours (the
 *               Empath) — the acknowledged coins are COMPUTED, not delivered.
 *   identity    FT-1402: ONE seat + a character, known outright — "this
 *               player IS the {noun}" (the Grandmother's grandchild). The
 *               FT-1397 learn-identity family's shape: a later member joins
 *               by adding a registry row here (kind + its own noun), never a
 *               bespoke wire — the residue already persists all game, which
 *               is this kind's whole point (her life hangs on that seat).
 *   character   FT-1412: ONE character, NO seat — "the executed was the
 *               {character}" (the Undertaker). The seat it is about is dead
 *               and named by daylight already, so nothing is acknowledged
 *               on any other coin: the whole dress lives on the told
 *               player's own chair (toldSeats answers nobody, the Chef's
 *               shape), and each other-night telling REPLACES the last (the
 *               store's highest-day rule). A night sent with no character
 *               (nobody was executed) says nothing and leaves the coin bare.
 */
export const TOLD_ROLES = {
  washerwoman: { kind: "pair", group: "Townsfolk" },
  librarian: { kind: "pair", group: "Outsider", zero: true },
  investigator: { kind: "pair", group: "Minion" },
  chef: { kind: "count" },
  empath: { kind: "neighbours" },
  grandmother: { kind: "identity", noun: "grandchild" },
  undertaker: { kind: "character" },
};

/**
 * The told roles whose DRESS has landed — grows one commit at a time
 * (washerwoman first, the reference), the FT-1384 HAS_ART idiom one card
 * over. The store's myTold getter answers for all five regardless (it is
 * data, not art); this list is what the SURFACES read, so an undressed
 * role renders exactly as it did before this card.
 */
export const TOLD_ART = [
  "washerwoman",
  "librarian",
  "investigator",
  "chef",
  "empath",
  // FT-1402: the Grandmother — the spectacles on the grandchild's coin,
  // the yarn line home, the told character's token at her own shoulder
  "grandmother",
  // FT-1412: the Undertaker — the executed character's token on his own
  // coin, his spade beneath it; no other seat is touched
  "undertaker",
];

/**
 * How long the bright telling pose holds before easing into the residue.
 * Long enough to read a sentence and glance at two coins; short enough that
 * the settled dress (the pose that persists all game) is what a night mostly
 * looks like. One number so the coins, the threads and the centre sentence
 * all settle on the same beat.
 */
export const TOLD_HOLD_MS = 7000;

/** Is this row a telling — a TOLD role's own row, sent? */
export function isToldRow(row) {
  return !!(row && row.sent && row.roleId && TOLD_ROLES[row.roleId]);
}

/** The delivered candidate seats, cleaned: real seat indexes only. */
export function toldTargets(row) {
  if (!row || !Array.isArray(row.targets)) return [];
  return row.targets.filter((t) => Number.isInteger(t) && t >= 0);
}

/**
 * The Empath's two LIVING neighbours — the nearest live seat walking each
 * way round the ring from `seat`. Recomputed live off the players array, so
 * a neighbour's death re-anchors the threads by itself (the board card's
 * rule). Returns 0, 1 or 2 distinct seats and never `seat` itself.
 */
export function liveNeighbours(players, seat) {
  const n = Array.isArray(players) ? players.length : 0;
  if (n < 2 || !Number.isInteger(seat) || seat < 0 || seat >= n) return [];
  const alive = (i) => players[i] && !players[i].isDead;
  const found = [];
  for (const step of [1, -1]) {
    for (let d = 1; d < n; d++) {
      const i = (seat + step * d + n * d) % n;
      if (i === seat) break;
      if (alive(i)) {
        found.push(i);
        break;
      }
    }
  }
  return found[0] === found[1] ? found.slice(0, 1) : found;
}

/**
 * The seats a telling ACKNOWLEDGES — the coins that take the role's mark.
 * Pair roles: the delivered candidates. The Empath: her live neighbours,
 * computed fresh. The Chef: nobody (the count is about seats she cannot
 * see into).
 */
export function toldSeats(told, players) {
  if (!told) return [];
  const spec = TOLD_ROLES[told.roleId];
  if (!spec) return [];
  // pair AND identity: the delivered seats ARE the acknowledged coins —
  // two candidates for a pair, the one known seat for an identity (FT-1402)
  if (spec.kind === "pair" || spec.kind === "identity") return told.targets;
  if (spec.kind === "neighbours") return liveNeighbours(players, told.seat);
  return [];
}

/**
 * FT-1401: THE NIGHTLY LEDGER — every number this role has ever been told,
 * one entry per night, oldest first. The Empath's coin wears these as the
 * accumulating history marks (a heart for 0, a red numeral above), so the
 * source is the same delivered rows the residue reads: the player's own
 * playerNight rows, which persist across nights and are cleared with the
 * game (Play again empties the log — the ledger is per-game by
 * construction). One row per day per seat per role (entryId's shape), so
 * days are unique without deduping. A row sent with no number entered
 * carries no knowledge and leaves no mark — the toldNumeral rule.
 */
export function toldNumberLedger(rows, roleId) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r && r.roleId === roleId && isToldRow(r))
    .map((r) => {
      if (r.number === null || r.number === undefined || r.number === "") {
        return null;
      }
      const n = Number(r.number);
      return Number.isFinite(n) ? { day: r.day, number: n } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.day - b.day);
}

/**
 * FT-1400: THE TOLD CHARACTER, resolved back to a role object so the dress
 * can wear its icon — the laundry line's hung token, the bookmark's, the
 * evidence string's. Only the character's NAME crosses the player-row
 * projection (characterId is withheld; the name is already the player's own
 * knowledge, so its icon is the same information in the role's own visual
 * language). Lookup by display name: the current script's roles first (a
 * homebrew may shadow a shipped name), the shipped table second. Null when
 * the telling names nobody (the Librarian's zero-Outsiders night) or the
 * name matches no role this client knows — a missing icon is never a broken
 * dress.
 */
export function toldCharacterRole(told, scriptRoles, baseById) {
  const name = told && told.characterName;
  if (!name) return null;
  const scan = (map) => {
    if (!map || typeof map.values !== "function") return null;
    for (const r of map.values()) {
      if (r && r.name === name) return r;
    }
    return null;
  };
  return scan(scriptRoles) || scan(baseById);
}

/**
 * THE SENTENCE — the telling's own words in the centre, and the smaller
 * line the residue keeps. `names` are the acknowledged seats' display
 * names, in target order.
 */
export function toldSentence(told, names, settled) {
  const spec = told && TOLD_ROLES[told.roleId];
  if (!spec) return "";
  // identity (FT-1402): the telling NAMES both halves outright — player and
  // character; the residue keeps the seat's name and the tie (the character
  // token lives on her own coin).
  if (spec.kind === "identity") {
    const name = names[0] || "";
    if (!name) return "";
    if (settled) return name + " · your " + spec.noun;
    return (
      name +
      " is your " +
      spec.noun +
      (told.characterName ? " — the " + told.characterName : "")
    );
  }
  // character (FT-1412): the Undertaker — one character, no seat. No
  // character delivered means nobody was executed: no sentence at all (the
  // face stays quiet and the coin bare, the card's rule).
  if (spec.kind === "character") {
    if (!told.characterName) return "";
    if (settled) return "executed · the " + told.characterName;
    return "the executed was the " + told.characterName;
  }
  if (spec.kind === "pair") {
    // the zero night (Librarian): nothing delivered means nobody is it
    if (!told.targets.length && !told.characterName) {
      return settled
        ? "no " + spec.group + "s in play"
        : "there are no " + spec.group + "s in play";
    }
    const who = told.characterName || "the " + spec.group;
    if (!settled) return "one of these two is the " + who;
    const pair = names.filter(Boolean).join(" & ");
    return pair ? "one of " + pair + " — the " + who : "the " + who;
  }
  const n = Number.isInteger(told.number) ? told.number : Number(told.number);
  if (!Number.isFinite(n)) return "";
  if (spec.kind === "count") {
    if (settled) {
      if (!n) return "no evil pairs · tonight";
      return n + " evil pair" + (n > 1 ? "s" : "") + " · side-by-side";
    }
    if (!n) return "no pairs of evil sit together";
    if (n === 1) return "1 pair of evil sits side-by-side";
    return n + " pairs of evil sit side-by-side";
  }
  // neighbours — the Empath
  if (settled) {
    if (!n) return "no evil neighbours · tonight";
    return n + " evil neighbour" + (n > 1 ? "s" : "") + " · tonight";
  }
  if (!n) return "neither of your two neighbours is evil";
  if (n === 1) return "1 of your two neighbours is evil";
  return "both of your neighbours are evil";
}
